/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/components/ExecutionTab.tsx
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why              | What Changed                          |
 * |---|------------------------|-------------------|------------------|---------------------------------------|
 * | 1 | 2026-06-18 (Phase 3b)  | Claude Sonnet 4.6 | 4-tab nav        | Created — run queue + Play/Pause/Stop |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx  (<ExecutionTab />)
 *
 * PROPS / EXPORTS
 * ---------------
 * ExecutionTabProps:
 *   adb               — live ADB connection (null = not connected)
 *   runQueue          — tests to run (pre-loaded from TestCasesTab "Run Selected")
 *   onQueueChange     — update the queue (add/remove)
 *   stepDelayMs       — delay between steps (shared with Record tab)
 *   deviceInfo        — model/serial/version for run records
 *   onSwitchToReports — "View in Reports →" post-run callback
 *
 * STATE MACHINE
 * -------------
 * idle → running → paused → running (resume) → complete
 *                         → stopped (from running or paused)
 * idle ← stopped / complete (reset)
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. ExecutionController is a mutable object shared between this component
 *    and replaySteps(). Pause/Stop mutate it; the replay loop polls it.
 * 2. Sequential multi-test: runs each test in runQueue one after another.
 *    Each test gets its own Run record in Dexie via saveRun().
 * 3. The mirror is NOT inside this component — it lives in App.tsx and is
 *    shown via CSS when the Execution tab is active.
 * 4. Replay on this tab always saves a Run record (unlike Record tab).
 *
 * =============================================================================
 */

import { useState, useRef, useCallback, useEffect } from "react";
import { replaySteps, makeController } from "../device/replayer.js";
import type { ReplayStepResult, ExecutionController } from "../device/replayer.js";
import { saveRun } from "../db/index.js";
import { swipeDirectionLabel } from "../App.js";
import { getCaptureEveryStep, getCaptureLogsOnFailure, getShowExecutionOverlay } from "./SettingsPage.js";
import type { ScriptResult } from "../device/replayer.js";
import type { TestCase } from "../db/schema.js";
import type { Adb } from "@yume-chan/adb";
import type { IRStep } from "@mimiq/ir";
import { generateId } from "@mimiq/ir";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db/index.js";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DeviceRunInfo {
  deviceModel: string;
  deviceSerial: string;
  androidVersion: string;
}

export interface ExecutionTabProps {
  adb: Adb | null;
  runQueue: TestCase[];
  onQueueChange: (queue: TestCase[]) => void;
  stepDelayMs: number;
  deviceInfo: DeviceRunInfo;
  onSwitchToReports: () => void;
  /** Called when a tap step completes with coordinates — triggers mirror ripple */
  onStepRipple?: (deviceX: number, deviceY: number) => void;
  /** Sandboxed JS runner for script steps (provided by Studio iframe) */
  scriptRunner?: (code: string, vars: Record<string, unknown>) => Promise<ScriptResult>;
  /** Loader for shared function library (from Dexie sharedFlows table) */
  sharedFlowLoader?: (id: string) => Promise<{ name: string; steps: import("@mimiq/ir").IRStep[]; parameters: { name: string; defaultValue?: string }[] } | undefined>;
}

type ExecState = "idle" | "running" | "paused" | "stopped" | "complete";

interface TestProgress {
  testCase: TestCase;
  results: ReplayStepResult[];
  executingIndex: number | null;
  status: "pending" | "running" | "passed" | "failed" | "stopped";
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function stepStatusIcon(s: ReplayStepResult["status"]): string {
  if (s === "ok") return "✓";
  if (s === "not_found") return "✗";
  return "!";
}

// ── Root component ────────────────────────────────────────────────────────────

export function ExecutionTab({
  adb, runQueue, onQueueChange, stepDelayMs, deviceInfo, onSwitchToReports, onStepRipple, scriptRunner, sharedFlowLoader,
}: ExecutionTabProps) {
  const [execState, setExecState] = useState<ExecState>("idle");
  const [progress, setProgress] = useState<TestProgress[]>([]);
  const [currentTestIdx, setCurrentTestIdx] = useState(0);
  const controllerRef = useRef<ExecutionController | null>(null);

  // For "Add to queue" picker
  const allTests = useLiveQuery(() => db.testCases.orderBy("name").toArray(), []) ?? [];
  const [showPicker, setShowPicker] = useState(false);

  const isRunning = execState === "running";
  const isPaused  = execState === "paused";
  const isDone    = execState === "complete" || execState === "stopped";
  const canPlay   = (execState === "idle" || isDone) && runQueue.length > 0 && adb !== null;
  const canPause  = execState === "running";
  const canResume = execState === "paused";
  const canStop   = execState === "running" || execState === "paused";

  const removeFromQueue = useCallback((id: string) => {
    if (execState !== "idle" && !isDone) return;
    onQueueChange(runQueue.filter((tc) => tc.id !== id));
  }, [execState, isDone, runQueue, onQueueChange]);

  const addToQueue = useCallback((tc: TestCase) => {
    if (runQueue.find((t) => t.id === tc.id)) return;
    onQueueChange([...runQueue, tc]);
    setShowPicker(false);
  }, [runQueue, onQueueChange]);

  // ── Play ──────────────────────────────────────────────────────────────────

  async function handlePlay() {
    if (!adb || runQueue.length === 0) return;

    const ctrl = makeController();
    controllerRef.current = ctrl;

    // Initialise progress for all tests
    const initialProgress: TestProgress[] = runQueue.map((tc) => ({
      testCase: tc, results: [], executingIndex: null, status: "pending",
    }));
    setProgress(initialProgress);
    setCurrentTestIdx(0);
    setExecState("running");

    for (let ti = 0; ti < runQueue.length; ti++) {
      if (ctrl.stopped) break;

      const tc = runQueue[ti]!;
      const steps: IRStep[] = tc.ir.flows[0]?.steps ?? [];

      setCurrentTestIdx(ti);
      setProgress((prev) => prev.map((p, i) =>
        i === ti ? { ...p, status: "running", results: [], executingIndex: null } : p,
      ));

      const runId = generateId(); // pre-generate so OPFS screenshot paths reference the same ID
      const captureEveryStep = getCaptureEveryStep();
      const captureLogsOnFailure = getCaptureLogsOnFailure();
      const showOverlay = getShowExecutionOverlay();
      const startedAt = Date.now();
      const stepResults = await replaySteps(steps, adb, {
        stepDelayMs,
        controller: ctrl,
        runId,
        captureEveryStep,
        captureLogsOnFailure,
        scriptRunner,
        sharedFlowLoader,
        onStepStart: (idx) => {
          setProgress((prev) => prev.map((p, i) =>
            i === ti ? { ...p, executingIndex: idx } : p,
          ));
        },
        onStepResult: (result) => {
          setProgress((prev) => prev.map((p, i) =>
            i === ti ? { ...p, results: [...p.results, result], executingIndex: null } : p,
          ));
          // Ripple on the mirror for tap steps with coordinates
          if (showOverlay && result.x !== undefined && result.y !== undefined && onStepRipple) {
            onStepRipple(result.x, result.y);
          }
        },
      });

      // Determine test outcome
      const failed = stepResults.some((r) => r.status !== "ok");
      const wasStopped = ctrl.stopped;
      const testStatus: TestProgress["status"] = wasStopped ? "stopped" : failed ? "failed" : "passed";

      setProgress((prev) => prev.map((p, i) =>
        i === ti ? { ...p, status: testStatus, executingIndex: null } : p,
      ));

      // Persist run to Dexie
      if (stepResults.length > 0 && !wasStopped) {
        saveRun({
          runId,
          testCaseId: tc.id,
          testCaseName: tc.name,
          startedAt,
          finishedAt: Date.now(),
          deviceModel: deviceInfo.deviceModel,
          deviceSerial: deviceInfo.deviceSerial,
          androidVersion: deviceInfo.androidVersion,
          stepResults,
          steps,
        }).catch(() => {});
      }

      if (ctrl.stopped) break;
    }

    setExecState(controllerRef.current?.stopped ? "stopped" : "complete");
    controllerRef.current = null;
  }

  function handlePause() {
    if (controllerRef.current) {
      controllerRef.current.paused = true;
      setExecState("paused");
    }
  }

  function handleResume() {
    if (controllerRef.current) {
      controllerRef.current.paused = false;
      setExecState("running");
    }
  }

  function handleStop() {
    if (controllerRef.current) {
      controllerRef.current.paused = false;
      controllerRef.current.stopped = true;
    }
    setExecState("stopped");
  }

  function handleReset() {
    setExecState("idle");
    setProgress([]);
    setCurrentTestIdx(0);
    controllerRef.current = null;
  }

  // ── Overall progress ──────────────────────────────────────────────────────

  const doneCount   = progress.filter((p) => p.status === "passed" || p.status === "failed").length;
  const passedCount = progress.filter((p) => p.status === "passed").length;
  const failedCount = progress.filter((p) => p.status === "failed").length;
  const totalSteps  = progress.reduce((s, p) => s + (p.testCase.ir.flows[0]?.steps.length ?? 0), 0);
  const doneSteps   = progress.reduce((s, p) => s + p.results.length, 0);

  return (
    <div className="exec-tab">
      {/* ── Run queue header ── */}
      <div className="exec-queue-header">
        <div className="exec-queue-chips">
          {runQueue.length === 0 ? (
            <span className="exec-queue-empty">No tests in queue — go to Test Cases and click Run Selected</span>
          ) : (
            runQueue.map((tc, i) => {
              const p = progress[i];
              return (
                <div
                  key={tc.id}
                  className={`exec-queue-chip${p?.status === "running" ? " exec-queue-chip--running" : ""}${p?.status === "passed" ? " exec-queue-chip--passed" : ""}${p?.status === "failed" ? " exec-queue-chip--failed" : ""}${p?.status === "stopped" ? " exec-queue-chip--stopped" : ""}`}
                >
                  {p?.status === "running" && <span className="exec-chip-spinner" />}
                  {p?.status === "passed"  && "✓ "}
                  {p?.status === "failed"  && "✗ "}
                  {p?.status === "stopped" && "■ "}
                  {tc.name}
                  {(execState === "idle" || isDone) && (
                    <button className="exec-chip-remove" onClick={() => removeFromQueue(tc.id)} title="Remove">✕</button>
                  )}
                </div>
              );
            })
          )}
          <button className="exec-add-btn" onClick={() => setShowPicker((s) => !s)} title="Add test to queue">
            + Add
          </button>
        </div>

        {/* Controls */}
        <div className="exec-controls">
          {!isRunning && !isPaused && (
            <button className="exec-ctrl-btn exec-ctrl-btn--play" disabled={!canPlay} onClick={() => void handlePlay()} title="Play">
              ▶ Play
            </button>
          )}
          {isRunning && (
            <button className="exec-ctrl-btn exec-ctrl-btn--pause" onClick={handlePause} title="Pause">
              ⏸ Pause
            </button>
          )}
          {isPaused && (
            <button className="exec-ctrl-btn exec-ctrl-btn--play" onClick={handleResume} title="Resume">
              ▶ Resume
            </button>
          )}
          {canStop && (
            <button className="exec-ctrl-btn exec-ctrl-btn--stop" onClick={handleStop} title="Stop">
              ■ Stop
            </button>
          )}
          {isDone && (
            <button className="exec-ctrl-btn" onClick={handleReset} title="Reset">
              ↺ Reset
            </button>
          )}
        </div>
      </div>

      {/* Test picker dropdown */}
      {showPicker && (
        <div className="exec-picker">
          <div className="exec-picker-header">
            <strong>Add test to queue</strong>
            <button className="exec-picker-close" onClick={() => setShowPicker(false)}>✕</button>
          </div>
          {allTests.filter((tc) => !runQueue.find((q) => q.id === tc.id)).map((tc) => (
            <button key={tc.id} className="exec-picker-item" onClick={() => addToQueue(tc)}>
              📄 {tc.name}
            </button>
          ))}
          {allTests.filter((tc) => !runQueue.find((q) => q.id === tc.id)).length === 0 && (
            <p className="exec-picker-empty">All saved tests are already in the queue.</p>
          )}
        </div>
      )}

      {/* Progress bar */}
      {(isRunning || isPaused || isDone) && totalSteps > 0 && (
        <div className="exec-progress-bar-outer">
          <div className="exec-progress-bar-inner" style={{ width: `${Math.round((doneSteps / totalSteps) * 100)}%` }} />
          <span className="exec-progress-label">
            {doneSteps}/{totalSteps} steps · {doneCount}/{runQueue.length} tests
            {passedCount > 0 && <span className="exec-prog-passed"> · ✓ {passedCount} passed</span>}
            {failedCount > 0 && <span className="exec-prog-failed"> · ✗ {failedCount} failed</span>}
            {isPaused && <span className="exec-prog-paused"> · ⏸ Paused</span>}
          </span>
        </div>
      )}

      {/* Not connected warning */}
      {!adb && (
        <div className="exec-no-device">
          <span>⚠</span> No device connected. Connect a phone on the <strong>Record</strong> tab first.
        </div>
      )}

      {/* Live step panels — one per test in queue */}
      <div className="exec-live-panels">
        {progress.map((p, ti) => (
          <LiveTestPanel key={p.testCase.id} progress={p} isCurrentTest={ti === currentTestIdx && isRunning} />
        ))}
      </div>

      {/* Post-run: View in Reports link */}
      {isDone && progress.length > 0 && (
        <div className="exec-done-banner">
          <span>
            Run complete — {passedCount} passed, {failedCount} failed
            {execState === "stopped" ? " (stopped early)" : ""}
          </span>
          <button className="btn btn--primary" onClick={onSwitchToReports}>
            View in Reports →
          </button>
        </div>
      )}
    </div>
  );
}

// ── LiveTestPanel ─────────────────────────────────────────────────────────────

function LiveTestPanel({ progress, isCurrentTest }: { progress: TestProgress; isCurrentTest: boolean }) {
  const { testCase, results, executingIndex, status } = progress;
  const steps = testCase.ir.flows[0]?.steps ?? [];
  const [collapsed, setCollapsed] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Auto-expand when this test starts running
  useEffect(() => {
    if (status === "running") setCollapsed(false);
  }, [status]);

  // Scroll this panel into view when it becomes the active test
  useEffect(() => {
    if (isCurrentTest && panelRef.current) {
      panelRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [isCurrentTest]);

  const headerClass = status === "passed" ? "exec-panel--passed"
    : status === "failed" ? "exec-panel--failed"
    : status === "running" ? "exec-panel--running"
    : status === "stopped" ? "exec-panel--stopped"
    : "exec-panel--pending";

  return (
    <div ref={panelRef} className={`exec-live-panel ${headerClass}`}>
      <div className="exec-panel-header" onClick={() => setCollapsed((c) => !c)}>
        <span className="exec-panel-chevron">{collapsed ? "▸" : "▾"}</span>
        {status === "running"  && <span className="exec-chip-spinner" />}
        {status === "passed"   && <span className="exec-panel-badge exec-panel-badge--passed">✓ Passed</span>}
        {status === "failed"   && <span className="exec-panel-badge exec-panel-badge--failed">✗ Failed</span>}
        {status === "stopped"  && <span className="exec-panel-badge exec-panel-badge--stopped">■ Stopped</span>}
        {status === "pending"  && <span className="exec-panel-badge exec-panel-badge--pending">○ Pending</span>}
        <span className="exec-panel-name">{testCase.name}</span>
        <span className="exec-panel-count">{results.length}/{steps.length} steps</span>
      </div>

      {!collapsed && (
        <ol className="exec-step-list">
          {steps.map((step, si) => {
            const result = results[si];
            const isExecuting = executingIndex === si;
            const isPending = si >= results.length && !isExecuting;

            return (
              <li
                key={step.id}
                className={`exec-step-row${result ? ` exec-step-row--${result.status}` : ""}${isExecuting ? " exec-step-row--executing" : ""}${isPending ? " exec-step-row--pending" : ""}`}
              >
                <span className="exec-step-num">{si + 1}</span>
                <span className="exec-step-type">{step.type}</span>
                <span className="exec-step-desc">
                  {step.type === "tap" || step.type === "doubleTap" || step.type === "longPress"
                   ? step.selector.description
                   : step.type === "launchApp" || step.type === "stopApp" ? step.packageName
                   : step.type === "typeText" ? (step.sensitive ? `🔒 "••••••••"` : `"${step.text}"`)
                   : step.type === "wait" ? `${step.durationMs}ms`
                   : step.type === "pressKey" ? step.key
                   : step.type === "swipe" ? swipeDirectionLabel(step.from, step.to)
                   : step.type === "screenshot" ? (step.label ?? "screenshot")
                   : step.type === "openUrl" || step.type === "deepLink" ? step.url
                   : step.type === "assert" ? `${step.selector.description} ${step.assertion}`
                   : step.type}
                </span>
                {result && (
                  <span className={`exec-step-badge exec-step-badge--${result.status}`}>
                    {stepStatusIcon(result.status)}
                    {result.matchedBy && <span className="exec-step-matched"> {result.matchedBy}</span>}
                  </span>
                )}
                {isExecuting && <span className="exec-chip-spinner exec-step-spinner" />}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
